import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Grid,
  Card,
  Tab,
  Tabs
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDiscountAPI } from '../../../hooks/useDiscountAPI';
import { CreateDiscountCodeParams } from './discounts.interface';
import toast from 'react-hot-toast';
import { VendorSelector } from '../../../components/VendorSelector/VendorSelector';
import { EmailEntry } from '../../../components/EmailEntry/EmailEntry';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { ProductSelector } from '../../../components/ProductSelector/ProductSelector';
import { TabContext, TabList } from '@mui/lab';
import { useEmailEntryStore } from '../../../zustandStore/store.emailentry';

interface CreateDiscountFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void;
}

const renderDatePicker = (label: string) => {
  const [value, setValue] = React.useState<Dayjs | null>(null);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label="label"
        value={value}
        onChange={(newValue) => {
          setValue(newValue);
        }}
      />
    </LocalizationProvider>
  );
};

type ViewType = 'VENDOR' | 'PRODUCTS' | 'EMAILS' | 'TABS' | 'BUYERS';

export const CreateDiscountForm: React.FC<CreateDiscountFormProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const { createDiscountCode, loading } = useDiscountAPI();
  const [vendorSelected, setVendorSelected] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<ViewType>();
  const [currentDate, setCurrentDate] = useState<Dayjs>(null);
  const [endDate, setEndDate] = useState<Dayjs>(null);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const emails = useEmailEntryStore((state) => state.emails);
  const [formData, setFormData] = useState<CreateDiscountCodeParams>({
    prefix: '',
    appliesTo: 'ALL_VENDORS_ALL_PRODUCTS',
    assignedTo: 'ALL_BUYERS',
    vendors: [],
    products: [],
    buyers: [],
    starts_at: null,
    ends_at: null,
    discount_value: 0
  });

  const handleInputChange = (field: keyof CreateDiscountCodeParams, val) => {
    setFormData((prevState) => {
      return {
        ...prevState,
        [field]: val
      };
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    debugger;
    event.preventDefault();

    // Basic validation
    if (!formData.prefix) {
      toast.error('Please enter a discount code prefix');
      return;
    }

    if (!formData.starts_at || !formData.ends_at) {
      toast.error('Please select start and end dates');
      return;
    }

    if (formData.ends_at.isBefore(formData.starts_at)) {
      toast.error('End date must be after start date');
      return;
    }

    if (formData.discount_value <= 0) {
      toast.error('Discount value must be greater than 0');
      return;
    }

    // Validate Selected Vendors and Products
    if (formData.appliesTo === 'SELECTED_VENDORS_AND_SELECTED_PRODUCTS') {
      if (
        (!formData.vendors || formData.vendors.length === 0) &&
        (!formData.products || formData.products.length === 0)
      ) {
        toast.error('Please select at least one vendor or product');
        return;
      }
    }

    // Validate Selected Vendors for ALL_PRODUCTS option
    if (
      formData.appliesTo === 'ALL_PRODUCTS' &&
      (!formData.vendors || formData.vendors.length === 0)
    ) {
      toast.error('Please add at least one vendor');
      return;
    }

    // Validate Selected Products for ALL_VENDORS option
    if (
      formData.appliesTo === 'ALL_VENDORS' &&
      (!formData.products || formData.products.length === 0)
    ) {
      toast.error('Please add at least one product');
      return;
    }

    // Validate Selected Buyers
    if (
      formData.assignedTo === 'SELECTED_BUYERS' &&
      (!formData.buyers || formData.buyers.length === 0)
    ) {
      toast.error('Please add at least one buyer email');
      return;
    }

    try {
      // Modify the form data to match the API requirements

      const success = await createDiscountCode(formData);
      if (success) {
        toast.success('Discount code created successfully');
        if (onSubmit) onSubmit(formData);
        handleClose();
      }
    } catch (error) {
      console.error('Error creating discount:', error);
      toast.error('Failed to create discount code');
    }
  };

  const handleClose = () => {
    setFormData({
      prefix: '',
      appliesTo: 'ALL_VENDORS_ALL_PRODUCTS',
      assignedTo: 'ALL_BUYERS',
      vendors: [],
      products: [],
      buyers: [],
      starts_at: null,
      ends_at: null,
      discount_value: 0
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        height: 'auto'
      }}
      maxWidth="lg"
      fullWidth
      aria-labelledby="create-discount-dialog-title"
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        id="create-discount-dialog-title"
      >
        Create New Discount Code
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: 'repeat(2, 1fr)',
                gridTemplateRows: 'min-content',
                gridAutoRows: 'min-content',
                width: '45%'
              }}
            >
              <Box>
                <TextField
                  fullWidth
                  label="Discount Code Prefix"
                  value={formData?.prefix}
                  onChange={(e) => handleInputChange('prefix', e.target.value)}
                  required
                  helperText="Enter a prefix for the discount code (e.g., SUMMER, WELCOME)"
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount Value"
                  value={formData?.discount_value}
                  onChange={(e) =>
                    handleInputChange(
                      'discount_value',
                      parseInt(e.target.value)
                    )
                  }
                  required
                  helperText="Enter discount"
                />
              </Box>

              <Box>
                <FormControl fullWidth required>
                  <InputLabel>Applies To</InputLabel>
                  <Select
                    value={formData.appliesTo}
                    onChange={(e) => {
                      handleInputChange('appliesTo', e.target.value);
                      if (
                        e.target.value ===
                        'SELECTED_VENDORS_AND_SELECTED_PRODUCTS'
                      ) {
                        setCurrentView('TABS');
                      }
                      if (e.target.value === 'ALL_PRODUCTS') {
                        setCurrentView('VENDOR');
                      }
                      if (e.target.value === 'ALL_VENDORS') {
                        handleInputChange('vendors', null);
                        setCurrentView('PRODUCTS');
                      }
                      if (e.target.value === 'ALL_VENDORS_ALL_PRODUCTS') {
                        handleInputChange('products', null);
                        handleInputChange('vendors', null);
                        setCurrentView(null);
                      }
                    }}
                    label="Applies To"
                  >
                    <MenuItem value="ALL_VENDORS_ALL_PRODUCTS">
                      All Vendors & Products
                    </MenuItem>
                    <MenuItem value="ALL_VENDORS">All Vendors</MenuItem>
                    <MenuItem value="ALL_PRODUCTS">All Products</MenuItem>
                    <MenuItem value="SELECTED_VENDORS_AND_SELECTED_PRODUCTS">
                      Selected Vendor and products
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <FormControl fullWidth required>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={formData.assignedTo}
                    onChange={(e) => {
                      handleInputChange('assignedTo', e.target.value);
                      console.log(e);
                      if (e.target.value === 'SELECTED_BUYERS') {
                        setCurrentView('BUYERS');
                      }
                      if (e.target.value === 'ALL_BUYERS') {
                        handleInputChange('buyers', null);
                        setCurrentView(null);
                      }
                    }}
                    label="Assigned To"
                  >
                    <MenuItem value="ALL_BUYERS">All Buyers</MenuItem>
                    <MenuItem value="SELECTED_BUYERS">Selected Buyers</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Start Date"
                    value={formData.starts_at}
                    openTo="month"
                    views={['year', 'month', 'day']}
                    yearsOrder="desc"
                    sx={{ minWidth: 250 }}
                    onChange={(value) => {
                      setCurrentDate(value);
                      setFormData((prevState: any) => {
                        return {
                          ...prevState,
                          starts_at: value ? value : null
                        };
                      });
                    }}
                  />{' '}
                </LocalizationProvider>
                {/*<TextField*/}
                {/*  fullWidth*/}
                {/*  type="datetime-local"*/}
                {/*  label="Start Date"*/}
                {/*  value={formData.starts_at}*/}
                {/*  onChange={handleInputChange('starts_at')}*/}
                {/*  required*/}
                {/*  InputLabelProps={{*/}
                {/*    shrink: true*/}
                {/*  }}*/}
                {/*/>*/}
              </Box>

              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    value={formData.ends_at}
                    openTo="month"
                    views={['year', 'month', 'day']}
                    yearsOrder="desc"
                    sx={{ minWidth: 250 }}
                    onChange={(value) => {
                      setEndDate(value);
                      setFormData((prevState: any) => {
                        return {
                          ...prevState,
                          ends_at: value ? value : null
                        };
                      });
                    }}
                  />{' '}
                </LocalizationProvider>
              </Box>

              {formData.appliesTo ===
                'SELECTED_VENDORS_AND_SELECTED_PRODUCTS' && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Product Selection
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Product selection functionality will be implemented based on
                    your product management system.
                  </Typography>
                </Box>
              )}

              {formData.assignedTo === 'SELECTED_BUYERS' && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Buyer Selection
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Buyer selection functionality based on email address entry.
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                borderLeft: '1px solid #eee',
                width: '1px'
              }}
            ></Box>
            <Box sx={{ width: '55%' }}>
              <Card
                sx={{
                  overflowY: 'scroll',
                  height: '450px',
                  p: '2%'
                }}
              >
                {currentView === 'VENDOR' && (
                  <VendorSelector
                    currentValue={formData.vendors}
                    onSelect={(e) =>
                      setFormData((prevState: any) => {
                        return {
                          ...prevState,
                          vendors: [...(prevState?.vendors || []), e]
                        };
                      })
                    }
                  />
                )}
                {currentView === 'PRODUCTS' && (
                  <ProductSelector
                    onVariantSelect={(variant) => {
                      setFormData((prevState: any) => {
                        return {
                          ...prevState,
                          products: [...prevState.products, variant]
                        };
                      });
                    }}
                  />
                )}
                {currentView === 'TABS' && (
                  <>
                    <Box
                      sx={{
                        width: '100%'
                      }}
                    >
                      <TabContext value={activeTabIndex}>
                        <TabList
                          onChange={(e, val) => {
                            setActiveTabIndex(val);
                          }}
                          aria-label="basic tabs example"
                        >
                          <Tab
                            sx={{ width: '50%' }}
                            value={0}
                            label="Products"
                          />
                          <Tab
                            sx={{ width: '50%' }}
                            value={1}
                            label="Vendors"
                          />
                        </TabList>
                      </TabContext>
                      <div>
                        {activeTabIndex === 0 && (
                          <ProductSelector
                            onVariantSelect={(e) => {
                              setFormData((prevState: any) => {
                                return {
                                  ...prevState,
                                  products: [...prevState?.products, e]
                                };
                              });
                            }}
                          />
                        )}
                        {activeTabIndex === 1 && (
                          <VendorSelector
                            currentValue={formData?.vendors ?? []}
                            onSelect={(e) =>
                              setFormData((prevState: any) => {
                                return {
                                  ...prevState,
                                  vendors: [
                                    ...(prevState?.vendors || {}),
                                    { vendorId: e }
                                  ]
                                };
                              })
                            }
                          />
                        )}
                      </div>
                    </Box>
                  </>
                )}

                {currentView === 'BUYERS' && (
                  <EmailEntry
                    list={formData.buyers}
                    onAdd={(e) => {
                      setFormData((prevState: any) => {
                        return {
                          ...prevState,
                          buyers: emails
                        };
                      });
                    }}
                  />
                )}
              </Card>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            {loading ? 'Creating...' : 'Create Discount'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
